/**
 * The strip under a WhatsApp thread's header: which booking this chat belongs to, and «ليس ضيفاً».
 * (tasks/whatsapp-inbox-4jawaly.md)
 *
 * Nothing here is required to answer a guest: a chat can be read and replied to while every button below is ignored.
 * It matters when staff want the door code, or the automatic match is wrong or missing. A phone match is a
 * suggestion, so the door code stays hidden until someone confirms it; the panel that shows it reads the real link.
 */
import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { conversationApi } from '../../services/conversationApi';
import { formatShortDate } from '../../utils/dateFormatter';

const stayText = (b) => (b.checkIn && b.checkOut ? `${formatShortDate(b.checkIn)} – ${formatShortDate(b.checkOut)}` : '');
const unitText = (p) => (p?.name ? (p.propertyCode ? `${p.propertyCode} · ${p.name}` : p.name) : '');

export default function WhatsAppThreadBar({ conversation, isSupervisor, onChanged, onLeftList }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [confirmHide, setConfirmHide] = useState(false);

  const id = conversation._id;

  const act = async (fn, failure, done = onChanged) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setSearching(false);
      setResults(null);
      setQuery('');
      setConfirmHide(false);
      done();
    } catch (err) {
      setError(err?.response?.data?.error || failure);
    } finally {
      setBusy(false);
    }
  };

  const search = async (e) => {
    e.preventDefault();
    if (query.trim().length < 2) return;
    setBusy(true);
    setError(null);
    try {
      const data = await conversationApi.whatsappBookings(id, query.trim());
      setResults(data.bookings || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'فشل في البحث عن الحجز');
    } finally {
      setBusy(false);
    }
  };

  const link = (bookingId) => act(() => conversationApi.whatsappLink(id, bookingId), 'فشل في ربط الحجز');
  const unlink = () => act(() => conversationApi.whatsappUnlink(id), 'فشل في إلغاء الربط');

  if (conversation.hiddenAt) {
    return (
      <div className="inbox-wa-bar" role="note">
        <span className="inbox-wa-text">هذه المحادثة مخفية لأنها ليست من ضيف، ولا تُحفظ رسائل هذا الرقم.</span>
        {isSupervisor && (
          <button type="button" className="inbox-release-btn" disabled={busy}
            onClick={() => act(() => conversationApi.whatsappRestore(id), 'فشل في إرجاع المحادثة', onLeftList)}>
            إرجاع المحادثة
          </button>
        )}
        {error && <span className="inbox-wa-error" role="alert">{error}</span>}
      </div>
    );
  }

  const suggested = conversation.suggestedBooking;
  const linked = !!conversation.matchedBookingId;

  return (
    <div className="inbox-wa-bar">
      {linked && (
        <>
          <span className="inbox-wa-text">مرتبطة بهذا الحجز{conversation.bookingLink?.byName ? <> بواسطة <bdi>{conversation.bookingLink.byName}</bdi></> : ''}.</span>
          <button type="button" className="inbox-release-btn" disabled={busy} onClick={unlink}>إلغاء الربط</button>
        </>
      )}

      {!linked && suggested && (
        <>
          <span className="inbox-wa-chip">مطابقة بالرقم</span>
          <span className="inbox-wa-text">أكّد الحجز لعرض الرمز.</span>
          <button type="button" className="inbox-claim-btn" disabled={busy} onClick={() => link(suggested.bookingId)}>تأكيد الحجز</button>
          <button type="button" className="inbox-release-btn" disabled={busy} onClick={unlink}>ليس هذا الحجز</button>
        </>
      )}

      {!linked && !suggested && !searching && (
        <button type="button" className="inbox-release-btn" disabled={busy} onClick={() => setSearching(true)}>ربط بحجز</button>
      )}

      {!confirmHide && (
        <button type="button" className="inbox-release-btn inbox-wa-end" disabled={busy} onClick={() => setConfirmHide(true)}>ليس ضيفاً</button>
      )}
      {confirmHide && (
        <span className="inbox-wa-confirm inbox-wa-end" role="group" aria-label="تأكيد إخفاء المحادثة">
          <span className="inbox-wa-text">ستُخفى المحادثة ولن تُحفظ رسائل هذا الرقم.</span>
          <button type="button" className="inbox-claim-btn" disabled={busy}
            onClick={() => act(() => conversationApi.whatsappNotGuest(id), 'فشل في إخفاء المحادثة', onLeftList)}>
            إخفاء
          </button>
          <button type="button" className="inbox-release-btn" disabled={busy} onClick={() => setConfirmHide(false)}>إلغاء</button>
        </span>
      )}

      {searching && !linked && (
        <form className="inbox-wa-search" onSubmit={search}>
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder="رقم الحجز أو اسم الضيف"
            aria-label="البحث عن حجز"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <button type="submit" className="inbox-claim-btn" disabled={busy || query.trim().length < 2}>
            {busy ? <Spinner animation="border" size="sm" /> : 'بحث'}
          </button>
          <button type="button" className="inbox-release-btn" disabled={busy}
            onClick={() => { setSearching(false); setResults(null); setQuery(''); }}>
            إغلاق
          </button>
          {results && results.length === 0 && <span className="inbox-wa-text">لا يوجد حجز بهذا البحث. جرّب رقم الحجز كاملاً.</span>}
          {results && results.length > 0 && (
            <ul className="inbox-wa-results">
              {results.map((b) => (
                <li key={b._id}>
                  <button type="button" className="inbox-wa-result" disabled={busy} onClick={() => link(b._id)}>
                    <bdi>{b.guestName}</bdi>
                    <span className="inbox-wa-result-sub">
                      {[unitText(b.property), stayText(b)].filter(Boolean).join(' · ')}
                      {b.confirmationCode && <> · <bdi dir="ltr">{b.confirmationCode}</bdi></>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </form>
      )}

      {error && <span className="inbox-wa-error" role="alert">{error}</span>}
    </div>
  );
}
